/**
 * Magic Link Verification Page
 * Handles verification of magic link tokens from email
 */

import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { managerVerifyMagicLink, managerSession, ApiClientError } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { useRunOnce } from '../../hooks/useRunOnce';
import { createLogger } from '@resida/shared';
import { Button } from '../../components/ui/button';
import { validateReturnTo } from '../../lib/return-to';

const logger = createLogger('landlord:pages:auth:MagicLinkVerifyPage');

type VerificationState = 'verifying' | 'success' | 'error';

export function MagicLinkVerifyPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState<VerificationState>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const verifyMagicLink = useCallback(async () => {
    const token = searchParams.get('token');

    if (!token) {
      logger.error('No token provided in URL');
      setState('error');
      setErrorMessage('Invalid magic link. No verification token found.');
      return;
    }

    try {
      logger.info('Verifying magic link token...');

      // Step 1: Verify the magic link token
      const result = await managerVerifyMagicLink(token);

      // Step 2: Fetch the session to get user info
      const session = await managerSession();
      if (session) {
        setSession(session);
        logger.info('Magic link verification successful', {
          email: session.identity.email,
        });
      }

      // Step 3: Show success state briefly
      const destination = validateReturnTo(result?.session?.returnTo ?? null);
      setState('success');

      // Step 4: Redirect to returnTo or dashboard after 1.5 seconds
      setTimeout(() => {
        navigate(destination, { replace: true });
      }, 1500);
    } catch (err) {
      logger.error('Magic link verification failed:', err);

      if (err instanceof ApiClientError) {
        if (err.code === 'INVALID_TOKEN') {
          setErrorMessage('This magic link is invalid or has expired. Please request a new one.');
        } else if (err.code === 'TOKEN_ALREADY_USED') {
          setErrorMessage('This magic link has already been used. Please request a new one.');
        } else if (err.code === 'TOKEN_EXPIRED') {
          setErrorMessage('This magic link has expired. Please request a new one.');
        } else {
          setErrorMessage(err.message || 'Verification failed. Please try again.');
        }
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }

      setState('error');
    }
  }, [navigate, searchParams, setSession]);

  // Single-use token: a re-run is rejected with 401.
  useRunOnce(verifyMagicLink);

  return (
    <div className="min-h-screen bg-muted/50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-sm border border-border p-10 text-center">
          {state === 'verifying' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-12 h-12 border-4 border-muted border-t-primary rounded-full animate-spin" />
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">Verifying Magic Link</h1>
              <p className="text-sm text-muted-foreground mb-6">Please wait while we verify your link...</p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success text-success-foreground text-4xl font-bold flex items-center justify-center">
                ✓
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">Success!</h1>
              <p className="text-sm text-muted-foreground mb-6">Redirecting to your dashboard...</p>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-destructive text-destructive-foreground text-4xl font-bold flex items-center justify-center">
                ✕
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">Verification Failed</h1>
              <div className="p-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md mb-4 text-left">
                {errorMessage}
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full min-h-[44px]"
              >
                Back to Login
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MagicLinkVerifyPage;
